import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import { factoryOrdersAPI, chefsAPI, productsAPI } from '../services/api';
import { AssignChefsModal } from '../components/UI/AssignChefsModal';

import {
  Search, Plus, Filter, Users, CheckCircle, XCircle, Package, User, Clock,
  AlertCircle, Eye, Trash2, Loader2, Download, RefreshCw,
  CheckSquare, Layers, TrendingUp, FileText, Calendar, Tag,
  ChevronDown, ChevronUp, Star, BarChart3, Target, Award,
  ShoppingCart, Edit, MoreVertical, ExternalLink, Printer, ChefHat,
  Warehouse, ArrowRight, ClipboardCheck, ThumbsUp, AlertTriangle
} from 'lucide-react';

// ========== الأنواع والواجهات ==========
interface Department {
  _id: string;
  name: string;
  nameEn?: string;
  code: string;
}

interface ChefInfo {
  _id: string;
  userId: string;
  displayName: string;
  name?: string;
  nameEn?: string;
  username?: string;
  department: Department[];
  status?: string;
}

interface Product {
  _id: string;
  name: string;
  nameEn?: string;
  unit: string;
  unitEn?: string;
  department: Department;
  price: number;
  code: string;
  isActive: boolean;
  minStockLevel?: number;
  maxStockLevel?: number;
}

interface OrderItem {
  _id: string;
  product: Product;
  quantity: number;
  status: 'pending' | 'assigned' | 'in_progress' | 'completed' | 'rejected' | 'cancelled';
  assignedTo?: {
    _id: string;
    name: string;
    nameEn?: string;
    username: string;
    role: string;
  };
  startedAt?: string;
  completedAt?: string;
  rejectReason?: string;
  notes?: string;
  unit?: string;
}

interface FactoryOrder {
  _id: string;
  id: string;
  orderNumber: string;
  status: 'requested' | 'pending' | 'approved' | 'in_production' | 'completed' | 'stocked' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  createdAt: string;
  updatedAt: string;
  createdBy: {
    _id: string;
    name: string;
    nameEn?: string;
    role: string;
  };
  items: OrderItem[];
  notes?: string;
  approvedBy?: {
    _id: string;
    name: string;
    nameEn?: string;
  };
  approvedAt?: string;
  stockedBy?: {
    _id: string;
    name: string;
    nameEn?: string;
  };
  stockedAt?: string;
}

interface CreateFormItem {
  productId: string;
  quantity: number;
  assignedTo?: string;
}

interface CreateFormData {
  notes: string;
  items: CreateFormItem[];
  priority: 'low' | 'medium' | 'high' | 'urgent';
}

interface AssignChefsForm {
  items: Array<{
    itemId: string;
    assignedTo?: string;
    rejected?: boolean;
    rejectReason?: string;
    quantity?: number;
    unit?: string;
  }>;
}

interface Statistics {
  totalOrders: number;
  requestedOrders: number;
  approvedOrders: number;
  productionOrders: number;
  completedOrders: number;
  stockedOrders: number;
  urgentOrders: number;
}

// ========== المكونات المساعدة ==========
const StatusBadge = ({ status, size = 'md' }: { status: string; size?: 'sm' | 'md' | 'lg' }) => {
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base'
  };

  const { language } = useLanguage();
  const isRtl = language === 'ar';

  const statusConfig = {
    requested: { color: 'bg-gray-100 text-gray-800 border-gray-200', icon: '📝', label: { ar: 'مطلوب', en: 'Requested' } },
    pending: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: '⏳', label: { ar: 'قيد الانتظار', en: 'Pending' } },
    approved: { color: 'bg-green-100 text-green-800 border-green-200', icon: '✅', label: { ar: 'معتمد', en: 'Approved' } },
    in_production: { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: '⚙️', label: { ar: 'في الإنتاج', en: 'In Production' } },
    completed: { color: 'bg-purple-100 text-purple-800 border-purple-200', icon: '🎯', label: { ar: 'مكتمل', en: 'Completed' } },
    stocked: { color: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: '📦', label: { ar: 'مخزن', en: 'Stocked' } },
    cancelled: { color: 'bg-red-100 text-red-800 border-red-200', icon: '❌', label: { ar: 'ملغى', en: 'Cancelled' } }
  };

  const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.requested;
  
  return (
    <span className={`inline-flex items-center gap-1.5 ${sizeClasses[size]} ${config.color} rounded-full border font-medium`}>
      <span>{config.icon}</span>
      <span>{config.label[isRtl ? 'ar' : 'en']}</span>
    </span>
  );
};

const ItemStatusBadge = ({ status, size = 'sm' }: { status: string; size?: 'sm' | 'md' | 'lg' }) => {
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm',
    lg: 'px-3 py-1.5 text-base'
  };

  const { language } = useLanguage();
  const isRtl = language === 'ar';

  const statusConfig = {
    pending: { color: 'bg-gray-100 text-gray-800 border-gray-200', icon: '⏳', label: { ar: 'قيد الانتظار', en: 'Pending' } },
    assigned: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: '👨‍🍳', label: { ar: 'معين', en: 'Assigned' } },
    in_progress: { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: '⚙️', label: { ar: 'قيد التنفيذ', en: 'In Progress' } },
    completed: { color: 'bg-green-100 text-green-800 border-green-200', icon: '✅', label: { ar: 'مكتمل', en: 'Completed' } },
    rejected: { color: 'bg-red-100 text-red-800 border-red-200', icon: '❌', label: { ar: 'مرفوض', en: 'Rejected' } },
    cancelled: { color: 'bg-gray-200 text-gray-700 border-gray-300', icon: '🚫', label: { ar: 'ملغى', en: 'Cancelled' } }
  };

  const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
  
  return (
    <span className={`inline-flex items-center gap-1 ${sizeClasses[size]} ${config.color} rounded-full border font-medium`}>
      <span>{config.icon}</span>
      <span>{config.label[isRtl ? 'ar' : 'en']}</span>
    </span>
  );
};

const PriorityBadge = ({ priority }: { priority: FactoryOrder['priority'] }) => {
  const priorityConfig = {
    urgent: { color: 'bg-red-100 text-red-800 border-red-200', icon: '🔥' },
    high: { color: 'bg-orange-100 text-orange-800 border-orange-200', icon: '⚠️' },
    medium: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: '📌' },
    low: { color: 'bg-green-100 text-green-800 border-green-200', icon: '📋' }
  };

  const config = priorityConfig[priority];
  const { language } = useLanguage();
  const isRtl = language === 'ar';
  
  const labels = {
    urgent: { ar: 'عاجل', en: 'Urgent' },
    high: { ar: 'مرتفع', en: 'High' },
    medium: { ar: 'متوسط', en: 'Medium' },
    low: { ar: 'منخفض', en: 'Low' }
  };

  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full border text-xs font-medium ${config.color}`}>
      <span>{config.icon}</span>
      <span>{labels[priority][isRtl ? 'ar' : 'en']}</span>
    </span>
  );
};

const StatCard = ({ 
  title, 
  value, 
  icon, 
  color, 
  subtitle, 
  onClick 
}: { 
  title: string; 
  value: number; 
  icon: React.ReactNode; 
  color: string;
  subtitle?: string;
  onClick?: () => void;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    whileHover={{ y: -5 }}
    onClick={onClick}
    className={`bg-white rounded-xl p-5 border border-gray-200 shadow-sm hover:shadow-md transition-all cursor-pointer ${onClick ? 'hover:border-gray-300' : ''}`}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-gray-600 mb-1">{title}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
      </div>
      <div className={`p-2.5 rounded-lg ${color}`}>
        {icon}
      </div>
    </div>
  </motion.div>
);

// ========== المكون الرئيسي ==========
const ProductionOrdersManagement: React.FC = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const isRtl = language === 'ar';

  // ========== States ==========
  const [orders, setOrders] = useState<FactoryOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [chefs, setChefs] = useState<ChefInfo[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  
  // UI States
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
  
  // Modal States
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<FactoryOrder | null>(null);
  const [orderDetailsModalOpen, setOrderDetailsModalOpen] = useState(false);
  
  // Form States
  const [createForm, setCreateForm] = useState<CreateFormData>({
    notes: '',
    items: [{ productId: '', quantity: 1 }],
    priority: 'medium'
  });
  
  const [assignFormData, setAssignFormData] = useState<AssignChefsForm>({
    items: []
  });

  const [productSearch, setProductSearch] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all');
  
  // Loading States
  const [submitting, setSubmitting] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [assignError, setAssignError] = useState<string>('');
  const [statsLoading, setStatsLoading] = useState(false);

  // ========== الدوال المساعدة ==========
  const calculateStatistics = useCallback((ordersList: FactoryOrder[]) => {
    const stats: Statistics = {
      totalOrders: ordersList.length,
      requestedOrders: ordersList.filter(order => order.status === 'requested').length,
      approvedOrders: ordersList.filter(order => order.status === 'approved').length,
      productionOrders: ordersList.filter(order => order.status === 'in_production').length,
      completedOrders: ordersList.filter(order => order.status === 'completed').length,
      stockedOrders: ordersList.filter(order => order.status === 'stocked').length,
      urgentOrders: ordersList.filter(order => order.priority === 'urgent').length
    };
    
    setStatistics(stats);
  }, []);

  // دالة مساعدة للحصول على معرف الطلب
  const getOrderId = useCallback((order: FactoryOrder): string => {
    // محاولة الحصول على المعرف بطرق مختلفة
    if (order._id && typeof order._id === 'string') return order._id;
    if (order.id && typeof order.id === 'string') return order.id;
    if (typeof order === 'string') return order;
    
    console.error('Cannot get order ID from:', order);
    throw new Error('Invalid order ID');
  }, []);

  // جلب بيانات الشيفات
  const fetchChefs = useCallback(async () => {
    try {
      const response = await chefsAPI.getAll({ 
        lang: language, 
        status: 'active'
      });

      let chefsData: ChefInfo[] = [];
      const responseData = response.data || response;
      
      if (Array.isArray(responseData)) {
        chefsData = responseData;
      } else if (responseData.data && Array.isArray(responseData.data)) {
        chefsData = responseData.data;
      }
      
      // تحويل البيانات إلى الصيغة المناسبة
      const processedChefsData = chefsData.map(chef => ({
        ...chef,
        userId: chef.user?._id || chef._id,
        displayName: isRtl 
          ? chef.user?.name || chef.name || chef.username || 'غير معروف'
          : chef.user?.nameEn || chef.nameEn || chef.name || chef.username || 'Unknown'
      }));
      
      setChefs(processedChefsData);
    } catch (error: any) {
      console.error('Error fetching chefs:', error);
      toast.error(isRtl ? 'خطأ في جلب بيانات الشيفات' : 'Error fetching chefs data');
    }
  }, [language, isRtl]);

  // جلب جميع المنتجات
  const fetchProducts = useCallback(async () => {
    try {
      const queryParams: any = { 
        lang: language, 
        isActive: true,
        limit: 1000
      };

      const response = await productsAPI.getAll(queryParams);
      
      let productsData: Product[] = [];
      if (response.data && Array.isArray(response.data)) {
        productsData = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        productsData = response.data.data;
      } else if (Array.isArray(response)) {
        productsData = response;
      }

      setProducts(productsData);
      setFilteredProducts(productsData);
    } catch (error: any) {
      console.error('Error fetching products:', error);
      toast.error(isRtl ? 'خطأ في جلب المنتجات' : 'Error fetching products');
    }
  }, [language]);

  // جلب الطلبات
  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      
      const queryParams: any = {
        lang: language,
        page: 1,
        limit: 100,
        sort: '-createdAt'
      };

      // للإدارة فقط - جلب جميع الطلبات
      const response = await factoryOrdersAPI.getAll(queryParams);
      
      let ordersData: FactoryOrder[] = [];
      if (response.data && Array.isArray(response.data)) {
        ordersData = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        ordersData = response.data.data;
      } else if (Array.isArray(response)) {
        ordersData = response;
      }
      
      // ترتيب الطلبات بحيث تكون المطلوبة أولاً
      ordersData.sort((a, b) => {
        const statusOrder = {
          'requested': 1,
          'pending': 2,
          'approved': 3,
          'in_production': 4,
          'completed': 5,
          'stocked': 6,
          'cancelled': 7
        };
        
        const statusA = statusOrder[a.status] || 8;
        const statusB = statusOrder[b.status] || 8;
        
        if (statusA !== statusB) {
          return statusA - statusB;
        }
        
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
      
      setOrders(ordersData);
      calculateStatistics(ordersData);
    } catch (error: any) {
      console.error('Error fetching orders:', error);
      toast.error(isRtl ? 'خطأ في جلب الطلبات' : 'Error fetching orders');
    } finally {
      setLoading(false);
      setRefreshing(false);
      setStatsLoading(false);
    }
  }, [language, calculateStatistics, isRtl]);

  // ========== useEffect ==========
  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'production_manager') {
      toast.error(isRtl ? 'غير مصرح بالوصول' : 'Access denied');
      return;
    }
    
    fetchOrders();
    fetchChefs();
    fetchProducts();
  }, [fetchOrders, fetchChefs, fetchProducts, user, isRtl]);

  useEffect(() => {
    // فلترة المنتجات حسب البحث والقسم
    let filtered = products;
    
    if (productSearch.trim()) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(productSearch.toLowerCase()) ||
        product.nameEn?.toLowerCase().includes(productSearch.toLowerCase()) ||
        product.code.toLowerCase().includes(productSearch.toLowerCase())
      );
    }
    
    if (selectedDepartment !== 'all') {
      filtered = filtered.filter(product => 
        product.department._id === selectedDepartment
      );
    }
    
    setFilteredProducts(filtered);
  }, [productSearch, selectedDepartment, products]);

  // ========== معالجة الأحداث ==========
  const handleCreateOrder = async () => {
    if (!user) {
      toast.error(isRtl ? 'يجب تسجيل الدخول أولاً' : 'Please login first');
      return;
    }

    try {
      setSubmitting(true);

      // التحقق من البيانات
      const validItems = createForm.items.filter(item => 
        item.productId && item.productId.trim() !== '' && item.quantity > 0
      );
      
      if (validItems.length === 0) {
        toast.error(isRtl ? 'يرجى إضافة منتجات صالحة' : 'Please add valid products');
        return;
      }

      const response = await factoryOrdersAPI.create({
        items: validItems.map(item => ({
          product: item.productId,
          quantity: item.quantity,
          assignedTo: item.assignedTo || undefined
        })),
        notes: createForm.notes.trim(),
        priority: createForm.priority
      });

      if (response.success || response._id) {
        toast.success(
          isRtl ? '✅ تم إنشاء طلب الإنتاج بنجاح' : '✅ Production order created successfully'
        );
        setCreateModalOpen(false);
        resetCreateForm();
        fetchOrders();
      } else {
        throw new Error(response.message || 'Unknown error');
      }
    } catch (error: any) {
      console.error('Error creating order:', error);
      toast.error(
        isRtl 
          ? `❌ خطأ في إنشاء الطلب: ${error.message || 'غير معروف'}` 
          : `❌ Error creating order: ${error.message || 'Unknown'}`
      );
    } finally {
      setSubmitting(false);
    }
  };

  const resetCreateForm = () => {
    setCreateForm({
      notes: '',
      items: [{ productId: '', quantity: 1 }],
      priority: 'medium'
    });
    setProductSearch('');
    setSelectedDepartment('all');
  };

  // إضافة عنصر جديد إلى الفورم
  const addNewItem = () => {
    setCreateForm(prev => ({
      ...prev,
      items: [...prev.items, { productId: '', quantity: 1 }]
    }));
  };

  // تحديث عنصر في الفورم
  const updateCreateFormItem = (index: number, field: keyof CreateFormItem, value: any) => {
    setCreateForm(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  // إزالة عنصر من الفورم
  const removeCreateFormItem = (index: number) => {
    setCreateForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // الموافقة على الطلب
  const handleApproveOrder = async (order: FactoryOrder) => {
    try {
      const orderId = getOrderId(order);
      
      console.log('Approving order:', {
        orderId,
        orderNumber: order.orderNumber,
        status: order.status
      });
      
      if (!orderId || orderId === 'undefined' || orderId === 'null') {
        toast.error(isRtl ? 'رقم الطلب غير صالح' : 'Invalid order ID');
        return;
      }
      
      if (!window.confirm(isRtl 
        ? `هل أنت متأكد من الموافقة على الطلب ${order.orderNumber}؟`
        : `Are you sure you want to approve order ${order.orderNumber}?`
      )) {
        return;
      }

      setActionLoading(`approve-${orderId}`);
      const response = await factoryOrdersAPI.approve(orderId);
      
      if (response.success) {
        toast.success(isRtl 
          ? `✅ تم الموافقة على الطلب ${order.orderNumber}` 
          : `✅ Order ${order.orderNumber} approved successfully`
        );
        fetchOrders();
      } else {
        throw new Error(response.message || 'Unknown error');
      }
    } catch (error: any) {
      console.error('Error approving order:', error);
      toast.error(isRtl 
        ? `❌ خطأ في الموافقة على الطلب: ${error.message || 'غير معروف'}` 
        : `❌ Error approving order: ${error.message || 'Unknown'}`
      );
    } finally {
      setActionLoading(null);
    }
  };

  // تعيين الشيفات
  const handleAssignChefs = async (order: FactoryOrder) => {
    try {
      const orderId = getOrderId(order);
      
      if (!orderId || orderId === 'undefined' || orderId === 'null') {
        toast.error(isRtl ? 'رقم الطلب غير صالح' : 'Invalid order ID');
        return;
      }
      
      setSubmitting(true);
      setAssignError('');
      
      // تحضير البيانات للتعيين
      const itemsToAssign = assignFormData.items
        .filter(item => item.assignedTo || item.rejected)
        .map(item => ({
          itemId: item.itemId,
          assignedTo: item.assignedTo,
          rejected: item.rejected || false,
          rejectReason: item.rejectReason || ''
        }));
      
      if (itemsToAssign.length === 0) {
        setAssignError(isRtl ? 'يرجى تحديد شيف أو رفض عنصر' : 'Please assign a chef or reject an item');
        return;
      }
      
      const response = await factoryOrdersAPI.assignChefs(orderId, { items: itemsToAssign });
      
      if (response.success) {
        toast.success(isRtl 
          ? `✅ تم تعيين الشيفات للطلب ${order.orderNumber}` 
          : `✅ Chefs assigned to order ${order.orderNumber} successfully`
        );
        setAssignModalOpen(false);
        setAssignFormData({ items: [] });
        fetchOrders();
      } else {
        setAssignError(response.message || (isRtl ? 'فشل التعيين' : 'Assignment failed'));
      }
    } catch (error: any) {
      console.error('Error assigning chefs:', error);
      setAssignError(error.message || (isRtl ? 'خطأ في التعيين' : 'Error assigning chefs'));
    } finally {
      setSubmitting(false);
    }
  };

  // تأكيد الإنتاج وإضافة للمخزون
  const handleCompleteProduction = async (order: FactoryOrder) => {
    try {
      const orderId = getOrderId(order);
      
      console.log('Completing production for order:', {
        orderId,
        orderNumber: order.orderNumber,
        status: order.status
      });
      
      if (!orderId || orderId === 'undefined' || orderId === 'null') {
        toast.error(isRtl ? 'رقم الطلب غير صالح' : 'Invalid order ID');
        return;
      }
      
      if (!window.confirm(isRtl 
        ? `هل أنت متأكد من تأكيد الإتمام وإضافة منتجات الطلب ${order.orderNumber} للمخزون؟` 
        : `Are you sure you want to confirm production and add products from order ${order.orderNumber} to inventory?`
      )) {
        return;
      }

      setActionLoading(`complete-${orderId}`);
      const response = await factoryOrdersAPI.confirmProduction(orderId);
      
      if (response.success) {
        toast.success(isRtl 
          ? `✅ تم تأكيد الإتمام وإضافة منتجات الطلب ${order.orderNumber} للمخزون` 
          : `✅ Production confirmed and products from order ${order.orderNumber} added to inventory`
        );
        fetchOrders();
      } else {
        throw new Error(response.message || 'Unknown error');
      }
    } catch (error: any) {
      console.error('Error completing production:', error);
      toast.error(isRtl 
        ? `❌ خطأ في تأكيد الإنتاج: ${error.message || 'غير معروف'}` 
        : `❌ Error confirming production: ${error.message || 'Unknown'}`
      );
    } finally {
      setActionLoading(null);
    }
  };

  // إلغاء الطلب
  const handleCancelOrder = async (order: FactoryOrder) => {
    try {
      const orderId = getOrderId(order);
      
      if (!orderId || orderId === 'undefined' || orderId === 'null') {
        toast.error(isRtl ? 'رقم الطلب غير صالح' : 'Invalid order ID');
        return;
      }
      
      const reason = prompt(isRtl ? 'يرجى إدخال سبب الإلغاء:' : 'Please enter cancel reason:');
      if (!reason || reason.trim().length < 3) {
        toast.warning(isRtl ? 'سبب الإلغاء يجب أن يكون 3 أحرف على الأقل' : 'Cancel reason must be at least 3 characters');
        return;
      }

      setActionLoading(`cancel-${orderId}`);
      const response = await factoryOrdersAPI.cancelOrder(orderId, { notes: reason });
      
      if (response.success) {
        toast.success(isRtl 
          ? `✅ تم إلغاء الطلب ${order.orderNumber}` 
          : `✅ Order ${order.orderNumber} cancelled successfully`
        );
        fetchOrders();
      } else {
        throw new Error(response.message || 'Unknown error');
      }
    } catch (error: any) {
      console.error('Error cancelling order:', error);
      toast.error(isRtl 
        ? `❌ خطأ في إلغاء الطلب: ${error.message || 'غير معروف'}` 
        : `❌ Error cancelling order: ${error.message || 'Unknown'}`
      );
    } finally {
      setActionLoading(null);
    }
  };

  // تحديث حالة العنصر (للعرض فقط)
  const handleItemStatusUpdate = async (orderId: string, itemId: string, status: 'start' | 'complete' | 'reject') => {
    // للإدارة لا يمكنها تغيير حالة العناصر مباشرة
    toast.info(isRtl ? 'الشيفات فقط يمكنهم تحديث حالة العناصر' : 'Only chefs can update item status');
  };

  const toggleOrderExpansion = (orderId: string) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedOrders(newExpanded);
  };

  const getChefsForProduct = useCallback((productId: string) => {
    const product = products.find(p => p._id === productId);
    if (!product || !product.department) return [];

    const productDepartmentId = product.department._id;
    
    return chefs
      .filter(chef => 
        Array.isArray(chef.department) &&
        chef.department.some(dept => dept._id === productDepartmentId)
      )
      .map(chef => ({
        value: chef.userId,
        label: chef.displayName || 'غير معروف',
        department: Array.isArray(chef.department) 
          ? chef.department.map(d => d.name).join(', ')
          : 'غير معروف'
      }));
  }, [products, chefs]);

  // تحضير نموذج تعيين الشيفات
  const prepareAssignForm = useCallback((order: FactoryOrder) => {
    const items = order.items
      .filter(item => item.status === 'pending' || item.status === 'assigned')
      .map(item => ({
        itemId: item._id,
        assignedTo: item.assignedTo?._id || '',
        rejected: false,
        rejectReason: '',
        quantity: item.quantity,
        unit: item.product?.unit || 'وحدة'
      }));
    
    setAssignFormData({ items });
    setSelectedOrder(order);
    setAssignModalOpen(true);
  }, []);

  // تحديث نموذج التعيين
  const updateAssignForm = (index: number, field: string, value: any) => {
    setAssignFormData(prev => ({
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  // فتح تفاصيل الطلب
  const openOrderDetails = (order: FactoryOrder) => {
    setSelectedOrder(order);
    setOrderDetailsModalOpen(true);
  };

  // ========== الفلترة ==========
  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      const matchesSearch = searchTerm === '' || 
        order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.notes?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.createdBy.name.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
      const matchesPriority = priorityFilter === 'all' || order.priority === priorityFilter;
      
      return matchesSearch && matchesStatus && matchesPriority;
    });
  }, [orders, searchTerm, statusFilter, priorityFilter]);

  const departments = useMemo(() => {
    const deptSet = new Set<string>();
    products.forEach(product => {
      if (product.department) {
        deptSet.add(product.department._id);
      }
    });
    
    return Array.from(deptSet).map(deptId => {
      const product = products.find(p => p.department._id === deptId);
      return {
        id: deptId,
        name: product?.department.name || 'غير معروف',
        nameEn: product?.department.nameEn || 'Unknown'
      };
    });
  }, [products]);

  // ========== عرض التحميل ==========
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">
            {isRtl ? 'جاري تحميل البيانات...' : 'Loading data...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-8 max-w-7xl mx-auto">
      {/* الرأس */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-8"
      >
        <div className={`flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-8 ${isRtl ? 'flex-row-reverse' : ''}`}>
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
              {isRtl ? 'إدارة طلبات الإنتاج' : 'Production Orders Management'}
            </h1>
            <p className="text-gray-600">
              {isRtl 
                ? 'إدارة ومراقبة جميع طلبات الإنتاج في المنشأة'
                : 'Manage and monitor all production orders in the facility'
              }
            </p>
          </div>
          
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setCreateModalOpen(true)}
              className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-xl px-5 py-3 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <Plus className="w-5 h-5" />
              {isRtl ? 'طلب إنتاج جديد' : 'New Production Order'}
            </button>
            
            <button
              onClick={() => {
                setRefreshing(true);
                setStatsLoading(true);
                fetchOrders();
                fetchChefs();
                fetchProducts();
              }}
              disabled={refreshing}
              className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-xl px-4 py-3 hover:bg-gray-50 transition-all duration-200 disabled:opacity-50"
            >
              {refreshing || statsLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <RefreshCw className="w-5 h-5" />
              )}
              {isRtl ? 'تحديث' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* الإحصائيات */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard
              title={isRtl ? 'إجمالي الطلبات' : 'Total Orders'}
              value={statistics.totalOrders}
              icon={<Package className="w-6 h-6 text-blue-600" />}
              color="bg-blue-50"
              subtitle={isRtl ? `${statistics.urgentOrders} عاجل` : `${statistics.urgentOrders} urgent`}
              onClick={() => setStatusFilter('all')}
            />
            
            <StatCard
              title={isRtl ? 'طلبات مطلوبة' : 'Requested Orders'}
              value={statistics.requestedOrders}
              icon={<FileText className="w-6 h-6 text-amber-600" />}
              color="bg-amber-50"
              subtitle={isRtl ? 'بانتظار الموافقة' : 'Pending approval'}
              onClick={() => setStatusFilter('requested')}
            />
            
            <StatCard
              title={isRtl ? 'طلبات معتمدة' : 'Approved Orders'}
              value={statistics.approvedOrders}
              icon={<CheckCircle className="w-6 h-6 text-green-600" />}
              color="bg-green-50"
              subtitle={isRtl ? 'جاهزة للتعيين' : 'Ready for assignment'}
              onClick={() => setStatusFilter('approved')}
            />
            
            <StatCard
              title={isRtl ? 'في الإنتاج' : 'In Production'}
              value={statistics.productionOrders}
              icon={<TrendingUp className="w-6 h-6 text-purple-600" />}
              color="bg-purple-50"
              subtitle={isRtl ? 'قيد التنفيذ' : 'In progress'}
              onClick={() => setStatusFilter('in_production')}
            />
          </div>
        )}

        {/* الفلاتر */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isRtl ? 'بحث' : 'Search'}
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={isRtl ? 'ابحث برقم الطلب، الملاحظات، أو اسم المنشئ...' : 'Search by order number, notes, or creator...'}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isRtl ? 'حالة الطلب' : 'Order Status'}
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              >
                <option value="all">{isRtl ? 'جميع الحالات' : 'All Statuses'}</option>
                <option value="requested">{isRtl ? 'مطلوب' : 'Requested'}</option>
                <option value="pending">{isRtl ? 'قيد الانتظار' : 'Pending'}</option>
                <option value="approved">{isRtl ? 'معتمد' : 'Approved'}</option>
                <option value="in_production">{isRtl ? 'في الإنتاج' : 'In Production'}</option>
                <option value="completed">{isRtl ? 'مكتمل' : 'Completed'}</option>
                <option value="stocked">{isRtl ? 'مخزن' : 'Stocked'}</option>
                <option value="cancelled">{isRtl ? 'ملغى' : 'Cancelled'}</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isRtl ? 'الأولوية' : 'Priority'}
              </label>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              >
                <option value="all">{isRtl ? 'جميع الأولويات' : 'All Priorities'}</option>
                <option value="urgent">{isRtl ? 'عاجل' : 'Urgent'}</option>
                <option value="high">{isRtl ? 'مرتفع' : 'High'}</option>
                <option value="medium">{isRtl ? 'متوسط' : 'Medium'}</option>
                <option value="low">{isRtl ? 'منخفض' : 'Low'}</option>
              </select>
            </div>
            
            <div className="flex items-end">
              <button
                onClick={() => setViewMode(viewMode === 'list' ? 'grid' : 'list')}
                className="w-full px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
              >
                {viewMode === 'list' ? (
                  <>
                    <BarChart3 className="w-5 h-5" />
                    {isRtl ? 'عرض شبكة' : 'Grid View'}
                  </>
                ) : (
                  <>
                    <Layers className="w-5 h-5" />
                    {isRtl ? 'عرض قائمة' : 'List View'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* قائمة الطلبات */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">
              {isRtl ? 'طلبات الإنتاج' : 'Production Orders'}
              <span className="text-sm font-normal text-gray-600 ml-2">
                ({filteredOrders.length} {isRtl ? 'طلب' : 'orders'})
              </span>
            </h3>
          </div>
        </div>
        
        {filteredOrders.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Package className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-medium text-gray-900 mb-2">
              {isRtl ? 'لا توجد طلبات' : 'No orders found'}
            </h3>
            <p className="text-gray-600 mb-8">
              {isRtl 
                ? 'لم يتم العثور على طلبات تطابق معايير البحث'
                : 'No orders matching your search criteria'
              }
            </p>
            <button
              onClick={() => setCreateModalOpen(true)}
              className="inline-flex items-center gap-2 bg-amber-600 text-white font-semibold rounded-xl px-6 py-3 hover:bg-amber-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              {isRtl ? 'إنشاء أول طلب' : 'Create First Order'}
            </button>
          </div>
        ) : viewMode === 'list' ? (
          <div className="divide-y divide-gray-200">
            {filteredOrders.map(order => {
              const isExpanded = expandedOrders.has(order._id);
              const pendingItems = order.items.filter(item => 
                item.status === 'pending' || item.status === 'assigned'
              ).length;
              const completedItems = order.items.filter(item => 
                item.status === 'completed'
              ).length;
              const rejectedItems = order.items.filter(item => 
                item.status === 'rejected'
              ).length;
              const orderId = getOrderId(order);
              const isLoading = actionLoading === `approve-${orderId}` || 
                               actionLoading === `complete-${orderId}` || 
                               actionLoading === `cancel-${orderId}`;
              
              return (
                <motion.div
                  key={order._id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <div className="px-6 py-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                            <Package className="w-5 h-5 text-amber-600" />
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900 font-mono">
                              {order.orderNumber}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <StatusBadge status={order.status} size="sm" />
                              <PriorityBadge priority={order.priority} />
                            </div>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                          <div className="text-sm">
                            <div className="text-gray-600">{isRtl ? 'المنشئ:' : 'Creator:'}</div>
                            <div className="font-medium">{order.createdBy.name}</div>
                          </div>
                          
                          <div className="text-sm">
                            <div className="text-gray-600">{isRtl ? 'التاريخ:' : 'Date:'}</div>
                            <div className="font-medium">
                              {new Date(order.createdAt).toLocaleDateString()}
                            </div>
                          </div>
                          
                          <div className="text-sm">
                            <div className="text-gray-600">{isRtl ? 'العناصر:' : 'Items:'}</div>
                            <div className="font-medium">
                              {order.items.length} {isRtl ? 'عنصر' : 'items'}
                            </div>
                          </div>
                          
                          <div className="text-sm">
                            <div className="text-gray-600">{isRtl ? 'التقدم:' : 'Progress:'}</div>
                            <div className="font-medium">
                              {completedItems}/{order.items.length} {isRtl ? 'مكتمل' : 'completed'}
                            </div>
                          </div>
                        </div>
                        
                        {order.notes && (
                          <p className="text-sm text-gray-600 mt-3 line-clamp-2">
                            <strong>{isRtl ? 'ملاحظات:' : 'Notes:'}</strong> {order.notes}
                          </p>
                        )}
                        
                        {order.approvedBy && (
                          <div className="text-xs text-gray-500 mt-2">
                            <strong>{isRtl ? 'تمت الموافقة بواسطة:' : 'Approved by:'}</strong> {order.approvedBy.name}
                            {order.approvedAt && ` - ${new Date(order.approvedAt).toLocaleDateString()}`}
                          </div>
                        )}
                        
                        {order.stockedBy && (
                          <div className="text-xs text-emerald-600 mt-2">
                            <strong>{isRtl ? 'تم التخزين بواسطة:' : 'Stocked by:'}</strong> {order.stockedBy.name}
                            {order.stockedAt && ` - ${new Date(order.stockedAt).toLocaleDateString()}`}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex flex-col items-end gap-2 ml-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openOrderDetails(order)}
                            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                            title={isRtl ? 'عرض التفاصيل' : 'View details'}
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                          
                          <button
                            onClick={() => toggleOrderExpansion(order._id)}
                            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                            title={isExpanded ? (isRtl ? 'إخفاء' : 'Hide') : (isRtl ? 'عرض العناصر' : 'Show items')}
                          >
                            {isExpanded ? (
                              <ChevronUp className="w-5 h-5" />
                            ) : (
                              <ChevronDown className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                        
                        {/* إجراءات الطلب حسب الحالة */}
                        <div className="flex flex-wrap gap-2 mt-2">
                          {order.status === 'requested' && (
                            <button
                              onClick={() => handleApproveOrder(order)}
                              disabled={isLoading}
                              className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                            >
                              {actionLoading === `approve-${orderId}` ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <CheckCircle className="w-3 h-3" />
                              )}
                              {isRtl ? 'موافقة' : 'Approve'}
                            </button>
                          )}
                          
                          {order.status === 'approved' && pendingItems > 0 && (
                            <button
                              onClick={() => prepareAssignForm(order)}
                              className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1"
                            >
                              <Users className="w-3 h-3" />
                              {isRtl ? 'تعيين' : 'Assign'}
                            </button>
                          )}
                          
                          {order.status === 'completed' && (
                            <button
                              onClick={() => handleCompleteProduction(order)}
                              disabled={isLoading}
                              className="px-3 py-1.5 bg-purple-600 text-white text-xs font-medium rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                            >
                              {actionLoading === `complete-${orderId}` ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Warehouse className="w-3 h-3" />
                              )}
                              {isRtl ? 'إضافة للمخزون' : 'Add to Stock'}
                            </button>
                          )}
                          
                          {order.status !== 'cancelled' && order.status !== 'stocked' && (
                            <button
                              onClick={() => handleCancelOrder(order)}
                              disabled={isLoading}
                              className="px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                            >
                              {actionLoading === `cancel-${orderId}` ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <XCircle className="w-3 h-3" />
                              )}
                              {isRtl ? 'إلغاء' : 'Cancel'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* العناصر المطوية */}
                    {isExpanded && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-4 pt-4 border-t border-gray-200"
                      >
                        <h4 className="font-medium text-gray-700 mb-3">
                          {isRtl ? 'عناصر الطلب' : 'Order Items'} ({order.items.length})
                        </h4>
                        <div className="space-y-3">
                          {order.items.map(item => (
                            <div key={item._id} className="bg-gray-50 rounded-lg p-4">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 bg-white rounded-lg border flex items-center justify-center">
                                      <Package className="w-4 h-4 text-gray-500" />
                                    </div>
                                    <div className="flex-1">
                                      <div className="font-medium text-gray-900">
                                        {item.product?.name || 'منتج غير معروف'}
                                      </div>
                                      <div className="text-sm text-gray-600 mt-1">
                                        {item.quantity} {item.product?.unit || 'وحدة'}
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <div className="flex items-center gap-3 mt-3">
                                    <ItemStatusBadge status={item.status} />
                                    
                                    {item.assignedTo && (
                                      <div className="text-xs text-gray-600 flex items-center gap-1">
                                        <User className="w-3 h-3" />
                                        <span className="font-medium">{item.assignedTo.name}</span>
                                      </div>
                                    )}
                                    
                                    {item.startedAt && (
                                      <div className="text-xs text-blue-600">
                                        <Clock className="w-3 h-3 inline mr-1" />
                                        {new Date(item.startedAt).toLocaleDateString()}
                                      </div>
                                    )}
                                    
                                    {item.completedAt && (
                                      <div className="text-xs text-green-600">
                                        <CheckCircle className="w-3 h-3 inline mr-1" />
                                        {new Date(item.completedAt).toLocaleDateString()}
                                      </div>
                                    )}
                                  </div>
                                  
                                  {item.rejectReason && (
                                    <div className="mt-2 text-xs text-red-600">
                                      <strong>{isRtl ? 'سبب الرفض:' : 'Reject reason:'}</strong> {item.rejectReason}
                                    </div>
                                  )}
                                  
                                  {item.notes && (
                                    <div className="mt-2 text-xs text-gray-600">
                                      <strong>{isRtl ? 'ملاحظات:' : 'Notes:'}</strong> {item.notes}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        ) : (
          // عرض Grid
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredOrders.map(order => {
                const completedItems = order.items.filter(item => 
                  item.status === 'completed'
                ).length;
                const progress = order.items.length > 0 
                  ? Math.round((completedItems / order.items.length) * 100) 
                  : 0;
                const orderId = getOrderId(order);
                const isLoading = actionLoading === `approve-${orderId}` || 
                                 actionLoading === `complete-${orderId}`;
                
                return (
                  <motion.div
                    key={order._id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-all overflow-hidden"
                  >
                    {/* شريط التقدم */}
                    <div className="mb-4">
                      <div className="flex justify-between text-xs text-gray-600 mb-1">
                        <span>{isRtl ? 'التقدم' : 'Progress'}</span>
                        <span>{progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h4 className="font-semibold text-gray-900 font-mono text-sm">
                          {order.orderNumber}
                        </h4>
                        <div className="flex items-center gap-2 mt-2">
                          <StatusBadge status={order.status} size="sm" />
                          <PriorityBadge priority={order.priority} />
                        </div>
                      </div>
                      <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                        <Package className="w-5 h-5 text-amber-600" />
                      </div>
                    </div>
                    
                    {order.notes && (
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                        {order.notes}
                      </p>
                    )}
                    
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">{isRtl ? 'المنشئ:' : 'Creator:'}</span>
                        <span className="font-medium">{order.createdBy.name}</span>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">{isRtl ? 'التاريخ:' : 'Date:'}</span>
                        <span>{new Date(order.createdAt).toLocaleDateString()}</span>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">{isRtl ? 'العناصر:' : 'Items:'}</span>
                        <span>{order.items.length} {isRtl ? 'عنصر' : 'items'}</span>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => openOrderDetails(order)}
                        className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                      >
                        <Eye className="w-4 h-4" />
                        {isRtl ? 'التفاصيل' : 'Details'}
                      </button>
                      
                      {/* إجراءات سريعة */}
                      {order.status === 'requested' && (
                        <button
                          onClick={() => handleApproveOrder(order)}
                          disabled={isLoading}
                          className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                          title={isRtl ? 'موافقة' : 'Approve'}
                        >
                          {actionLoading === `approve-${orderId}` ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <CheckCircle className="w-4 h-4" />
                          )}
                        </button>
                      )}
                      
                      {order.status === 'completed' && (
                        <button
                          onClick={() => handleCompleteProduction(order)}
                          disabled={isLoading}
                          className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                          title={isRtl ? 'إضافة للمخزون' : 'Add to Stock'}
                        >
                          {actionLoading === `complete-${orderId}` ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Warehouse className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ========== النوافذ المنبثقة ========== */}

      {/* نافذة إنشاء طلب جديد */}
      <AnimatePresence>
        {createModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 overflow-y-auto"
          >
            <div className="flex items-center justify-center min-h-screen p-4">
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="inline-block w-full max-w-5xl bg-white rounded-2xl shadow-2xl overflow-hidden"
              >
                {/* Header */}
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-5">
                  <div className="flex items-center justify-between">
                    <div className="text-white">
                      <h2 className="text-2xl font-bold">
                        {isRtl ? 'إنشاء طلب إنتاج جديد' : 'Create New Production Order'}
                      </h2>
                      <p className="text-amber-100 text-sm mt-1">
                        {isRtl 
                          ? 'اختر المنتجات المطلوبة للإنتاج'
                          : 'Select required products for production'
                        }
                      </p>
                    </div>
                    <button
                      onClick={() => !submitting && setCreateModalOpen(false)}
                      className="text-white hover:text-amber-200 p-1 rounded-full hover:bg-white/10 transition-colors"
                      disabled={submitting}
                    >
                      <XCircle className="w-6 h-6" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 max-h-[70vh] overflow-y-auto">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column - Products Selection */}
                    <div className="lg:col-span-2">
                      <div className="mb-6">
                        <div className="flex flex-col sm:flex-row gap-3 mb-4">
                          <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {isRtl ? 'بحث عن منتج' : 'Search Product'}
                            </label>
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                              <input
                                type="text"
                                value={productSearch}
                                onChange={(e) => setProductSearch(e.target.value)}
                                placeholder={isRtl ? 'ابحث باسم المنتج أو الكود...' : 'Search by product name or code...'}
                                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                              />
                            </div>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {isRtl ? 'القسم' : 'Department'}
                            </label>
                            <select
                              value={selectedDepartment}
                              onChange={(e) => setSelectedDepartment(e.target.value)}
                              className="w-full sm:w-auto px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            >
                              <option value="all">{isRtl ? 'جميع الأقسام' : 'All Departments'}</option>
                              {departments.map(dept => (
                                <option key={dept.id} value={dept.id}>
                                  {isRtl ? dept.name : dept.nameEn}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        
                        {/* Products Grid */}
                        <div className="bg-gray-50 rounded-xl border border-gray-300 p-4 max-h-[300px] overflow-y-auto">
                          {filteredProducts.length === 0 ? (
                            <div className="text-center py-12">
                              <ShoppingCart className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                              <p className="text-gray-600">
                                {productSearch || selectedDepartment !== 'all'
                                  ? (isRtl ? 'لا توجد منتجات تطابق البحث' : 'No products match your search')
                                  : (isRtl ? 'لا توجد منتجات متاحة' : 'No products available')
                                }
                              </p>
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {filteredProducts.map(product => {
                                const isInCart = createForm.items.some(item => item.productId === product._id);
                                
                                return (
                                  <motion.div
                                    key={product._id}
                                    whileHover={{ scale: 1.01 }}
                                    whileTap={{ scale: 0.99 }}
                                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                                      isInCart
                                        ? 'border-amber-500 bg-amber-50'
                                        : 'border-gray-200 bg-white hover:border-gray-300'
                                    }`}
                                    onClick={() => {
                                      if (!isInCart) {
                                        setCreateForm(prev => ({
                                          ...prev,
                                          items: [...prev.items, { productId: product._id, quantity: 1, assignedTo: '' }]
                                        }));
                                      }
                                    }}
                                  >
                                    <div className="flex items-start justify-between">
                                      <div>
                                        <h4 className="font-semibold text-gray-900">{product.name}</h4>
                                        <div className="flex items-center gap-2 mt-2">
                                          <span className="text-xs px-2 py-1 bg-gray-100 rounded text-gray-600">
                                            {product.code}
                                          </span>
                                          <span className="text-xs px-2 py-1 bg-blue-100 rounded text-blue-600">
                                            {isRtl ? product.department.name : product.department.nameEn || product.department.name}
                                          </span>
                                        </div>
                                      </div>
                                      {isInCart && (
                                        <CheckCircle className="w-5 h-5 text-amber-500" />
                                      )}
                                    </div>
                                    <div className="flex items-center justify-between mt-4">
                                      <div className="text-sm text-gray-600">
                                        {product.unit} • {product.price} $
                                      </div>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (!isInCart) {
                                            setCreateForm(prev => ({
                                              ...prev,
                                              items: [...prev.items, { productId: product._id, quantity: 1, assignedTo: '' }]
                                            }));
                                          }
                                        }}
                                        disabled={isInCart}
                                        className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                                          isInCart
                                            ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                                            : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                                        }`}
                                      >
                                        {isInCart
                                          ? (isRtl ? 'مضاف' : 'Added')
                                          : (isRtl ? 'إضافة' : 'Add')
                                        }
                                      </button>
                                    </div>
                                  </motion.div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Right Column - Order Details */}
                    <div>
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6">
                        <h3 className="font-semibold text-amber-900 mb-4">
                          {isRtl ? 'تفاصيل الطلب' : 'Order Details'}
                        </h3>
                        
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {isRtl ? 'ملاحظات (اختياري)' : 'Notes (optional)'}
                            </label>
                            <textarea
                              value={createForm.notes}
                              onChange={(e) => setCreateForm({...createForm, notes: e.target.value})}
                              placeholder={isRtl ? 'ملاحظات حول الطلب...' : 'Notes about the order...'}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                              rows={3}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {isRtl ? 'الأولوية' : 'Priority'}
                            </label>
                            <div className="grid grid-cols-2 gap-3">
                              {(['urgent', 'high', 'medium', 'low'] as const).map((priority) => (
                                <button
                                  key={priority}
                                  type="button"
                                  onClick={() => setCreateForm({...createForm, priority})}
                                  className={`p-3 rounded-lg border-2 text-center transition-all ${
                                    createForm.priority === priority
                                      ? priority === 'urgent' 
                                        ? 'border-red-500 bg-red-50 text-red-700' :
                                        priority === 'high'
                                        ? 'border-orange-500 bg-orange-50 text-orange-700' :
                                        priority === 'medium'
                                        ? 'border-yellow-500 bg-yellow-50 text-yellow-700' :
                                        'border-green-500 bg-green-50 text-green-700'
                                      : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300'
                                  }`}
                                >
                                  <div className="text-sm font-medium mb-1">
                                    {priority === 'urgent' ? (isRtl ? 'عاجل' : 'Urgent') :
                                     priority === 'high' ? (isRtl ? 'مرتفع' : 'High') :
                                     priority === 'medium' ? (isRtl ? 'متوسط' : 'Medium') :
                                     (isRtl ? 'منخفض' : 'Low')}
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Selected Items */}
                      <div className="bg-white border border-gray-200 rounded-xl p-5">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="font-semibold text-gray-900">
                            {isRtl ? 'المنتجات المحددة' : 'Selected Products'}
                            <span className="text-sm font-normal text-gray-600 ml-2">
                              ({createForm.items.filter(i => i.productId).length})
                            </span>
                          </h3>
                          <button
                            type="button"
                            onClick={addNewItem}
                            className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-lg hover:bg-green-200 transition-colors"
                          >
                            <Plus className="w-3 h-3 inline mr-1" />
                            {isRtl ? 'إضافة منتج' : 'Add Product'}
                          </button>
                        </div>
                        
                        {createForm.items.filter(item => item.productId).length === 0 ? (
                          <div className="text-center py-6">
                            <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500 text-sm">
                              {isRtl ? 'لم يتم اختيار منتجات' : 'No products selected'}
                            </p>
                            <p className="text-gray-400 text-xs mt-1">
                              {isRtl ? 'اختر منتجات من القائمة' : 'Select products from the list'}
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-3 max-h-[250px] overflow-y-auto pr-2">
                            {createForm.items
                              .filter(item => item.productId)
                              .map((item, index) => {
                                const product = products.find(p => p._id === item.productId);
                                if (!product) return null;
                                
                                const chefsForProduct = getChefsForProduct(product._id);
                                
                                return (
                                  <div key={index} className="bg-gray-50 rounded-lg p-4">
                                    <div className="flex items-start justify-between mb-3">
                                      <div>
                                        <div className="font-medium text-sm">{product.name}</div>
                                        <div className="text-xs text-gray-500 mt-1">
                                          {isRtl ? product.department.name : product.department.nameEn || product.department.name}
                                        </div>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => removeCreateFormItem(index)}
                                        className="text-red-500 hover:text-red-700"
                                      >
                                        <XCircle className="w-4 h-4" />
                                      </button>
                                    </div>
                                    
                                    <div className="space-y-3">
                                      <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">
                                          {isRtl ? 'الكمية' : 'Quantity'}
                                        </label>
                                        <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const newQuantity = Math.max(1, item.quantity - 1);
                                              updateCreateFormItem(index, 'quantity', newQuantity);
                                            }}
                                            className="px-3 py-1.5 bg-gray-100 text-gray-700 hover:bg-gray-200"
                                          >
                                            -
                                          </button>
                                          <input
                                            type="number"
                                            min="1"
                                            value={item.quantity}
                                            onChange={(e) => {
                                              const newQuantity = Math.max(1, parseInt(e.target.value) || 1);
                                              updateCreateFormItem(index, 'quantity', newQuantity);
                                            }}
                                            className="w-full px-3 py-1.5 text-center border-0 focus:outline-none focus:ring-0"
                                          />
                                          <button
                                            type="button"
                                            onClick={() => {
                                              updateCreateFormItem(index, 'quantity', item.quantity + 1);
                                            }}
                                            className="px-3 py-1.5 bg-gray-100 text-gray-700 hover:bg-gray-200"
                                          >
                                            +
                                          </button>
                                        </div>
                                      </div>
                                      
                                      {chefsForProduct.length > 0 && (
                                        <div>
                                          <label className="block text-xs font-medium text-gray-600 mb-1">
                                            {isRtl ? 'تعيين شيف (اختياري)' : 'Assign Chef (optional)'}
                                          </label>
                                          <select
                                            value={item.assignedTo || ''}
                                            onChange={(e) => updateCreateFormItem(index, 'assignedTo', e.target.value)}
                                            className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-amber-500"
                                          >
                                            <option value="">{isRtl ? 'غير معين' : 'Not assigned'}</option>
                                            {chefsForProduct.map(chef => (
                                              <option key={chef.value} value={chef.value}>
                                                {chef.label}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-6 py-5 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                    <div className="text-sm text-gray-600">
                      {isRtl 
                        ? `${createForm.items.filter(i => i.productId).length} منتج • ${createForm.items.reduce((sum, item) => sum + item.quantity, 0)} وحدة`
                        : `${createForm.items.filter(i => i.productId).length} products • ${createForm.items.reduce((sum, item) => sum + item.quantity, 0)} units`
                      }
                    </div>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => !submitting && setCreateModalOpen(false)}
                        className="px-6 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                        disabled={submitting}
                      >
                        {isRtl ? 'إلغاء' : 'Cancel'}
                      </button>
                      <button
                        type="button"
                        onClick={handleCreateOrder}
                        disabled={submitting || createForm.items.filter(i => i.productId).length === 0}
                        className="px-6 py-2.5 bg-amber-600 text-white font-semibold rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            {isRtl ? 'جاري الإنشاء...' : 'Creating...'}
                          </>
                        ) : (
                          <>
                            <Plus className="w-5 h-5" />
                            {isRtl ? 'إنشاء الطلب' : 'Create Order'}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* نافذة تعيين الشيفات */}
      <AssignChefsModal
        isOpen={assignModalOpen}
        onClose={() => {
          setAssignModalOpen(false);
          setAssignError('');
        }}
        selectedOrder={selectedOrder}
        assignFormData={assignFormData}
        chefs={chefs}
        error={assignError}
        submitting={submitting ? 'assigning' : null}
        assignChefs={() => selectedOrder && handleAssignChefs(selectedOrder)}
        setAssignForm={(formData) => setAssignFormData(formData)}
        isRtl={isRtl}
      />

      {/* نافذة تفاصيل الطلب */}
      <AnimatePresence>
        {orderDetailsModalOpen && selectedOrder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 overflow-y-auto"
          >
            <div className="flex items-center justify-center min-h-screen p-4">
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="inline-block w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden"
              >
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-500 to-cyan-500 px-6 py-5">
                  <div className="flex items-center justify-between">
                    <div className="text-white">
                      <h2 className="text-2xl font-bold">
                        {selectedOrder.orderNumber}
                      </h2>
                      <p className="text-blue-100 text-sm mt-1">
                        {isRtl ? 'تفاصيل طلب الإنتاج' : 'Production Order Details'}
                      </p>
                    </div>
                    <button
                      onClick={() => setOrderDetailsModalOpen(false)}
                      className="text-white hover:text-blue-200 p-1 rounded-full hover:bg-white/10 transition-colors"
                    >
                      <XCircle className="w-6 h-6" />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 max-h-[70vh] overflow-y-auto">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {isRtl ? 'حالة الطلب' : 'Order Status'}
                        </label>
                        <div className="flex items-center gap-2">
                          <StatusBadge status={selectedOrder.status} size="md" />
                          <PriorityBadge priority={selectedOrder.priority} />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {isRtl ? 'تاريخ الإنشاء' : 'Created Date'}
                        </label>
                        <p className="text-gray-900">
                          {new Date(selectedOrder.createdAt).toLocaleString()}
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {isRtl ? 'تم الإنشاء بواسطة' : 'Created By'}
                        </label>
                        <p className="text-gray-900">{selectedOrder.createdBy.name}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      {selectedOrder.approvedBy && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {isRtl ? 'تمت الموافقة بواسطة' : 'Approved By'}
                          </label>
                          <p className="text-gray-900">{selectedOrder.approvedBy.name}</p>
                          {selectedOrder.approvedAt && (
                            <p className="text-sm text-gray-600">
                              {new Date(selectedOrder.approvedAt).toLocaleString()}
                            </p>
                          )}
                        </div>
                      )}
                      
                      {selectedOrder.stockedBy && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {isRtl ? 'تم التخزين بواسطة' : 'Stocked By'}
                          </label>
                          <p className="text-gray-900">{selectedOrder.stockedBy.name}</p>
                          {selectedOrder.stockedAt && (
                            <p className="text-sm text-gray-600">
                              {new Date(selectedOrder.stockedAt).toLocaleString()}
                            </p>
                          )}
                        </div>
                      )}
                      
                      {selectedOrder.notes && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {isRtl ? 'ملاحظات' : 'Notes'}
                          </label>
                          <p className="text-gray-900 whitespace-pre-line">{selectedOrder.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-200 pt-6">
                    <h3 className="font-semibold text-gray-900 mb-4">
                      {isRtl ? 'عناصر الطلب' : 'Order Items'} ({selectedOrder.items.length})
                    </h3>
                    <div className="space-y-4">
                      {selectedOrder.items.map(item => {
                        const statusColors = {
                          pending: 'bg-gray-100 text-gray-800',
                          assigned: 'bg-yellow-100 text-yellow-800',
                          in_progress: 'bg-blue-100 text-blue-800',
                          completed: 'bg-green-100 text-green-800',
                          rejected: 'bg-red-100 text-red-800',
                          cancelled: 'bg-gray-200 text-gray-700'
                        };
                        
                        return (
                          <div key={item._id} className="bg-gray-50 rounded-xl p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-start gap-3">
                                  <div className="w-10 h-10 bg-white rounded-lg border flex items-center justify-center">
                                    <Package className="w-5 h-5 text-gray-500" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-start justify-between">
                                      <div>
                                        <h4 className="font-semibold text-gray-900">
                                          {item.product?.name || 'منتج غير معروف'}
                                        </h4>
                                        <div className="text-sm text-gray-600 mt-1">
                                          {item.quantity} {item.product?.unit || 'وحدة'}
                                        </div>
                                      </div>
                                      <span className={`text-xs px-2.5 py-1 rounded-full ${statusColors[item.status]}`}>
                                        {factoryOrdersAPI.getItemStatusText(item.status, isRtl)}
                                      </span>
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-4 mt-3">
                                      {item.assignedTo && (
                                        <div className="text-sm">
                                          <div className="text-gray-600">{isRtl ? 'معين لـ:' : 'Assigned to:'}</div>
                                          <div className="font-medium">{item.assignedTo.name}</div>
                                        </div>
                                      )}
                                      
                                      {item.startedAt && (
                                        <div className="text-sm">
                                          <div className="text-gray-600">{isRtl ? 'تاريخ البدء:' : 'Start Date:'}</div>
                                          <div className="font-medium">
                                            {new Date(item.startedAt).toLocaleString()}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {item.completedAt && (
                                        <div className="text-sm">
                                          <div className="text-gray-600">{isRtl ? 'تاريخ الإكمال:' : 'Completion Date:'}</div>
                                          <div className="font-medium">
                                            {new Date(item.completedAt).toLocaleString()}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {item.rejectReason && (
                                        <div className="col-span-2 text-sm">
                                          <div className="text-gray-600">{isRtl ? 'سبب الرفض:' : 'Reject Reason:'}</div>
                                          <div className="font-medium text-red-600">{item.rejectReason}</div>
                                        </div>
                                      )}
                                      
                                      {item.notes && (
                                        <div className="col-span-2 text-sm">
                                          <div className="text-gray-600">{isRtl ? 'ملاحظات العنصر:' : 'Item Notes:'}</div>
                                          <div className="font-medium">{item.notes}</div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-600">
                      {selectedOrder.updatedAt && (
                        <span>
                          {isRtl ? 'آخر تحديث:' : 'Last updated:'} {new Date(selectedOrder.updatedAt).toLocaleString()}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setOrderDetailsModalOpen(false)}
                        className="px-6 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        {isRtl ? 'إغلاق' : 'Close'}
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProductionOrdersManagement;